import type { ForteXApi, LayerChange, LayerState, LayerSummary, MapNodeSettings, MapRootName, MasterDataModel, ObjectLayerInfo } from "@fortex/api";
import { catalogueKey } from "./types";
import type {
  Catalogue,
  CatalogueBounds,
  CatalogueBrush,
  CatalogueEntry,
  CatalogueIcon,
  CatalogueKind,
  CataloguePen,
  CatalogueProblem,
  CatalogueRoot,
  CatalogueStyle
} from "./types";

export const knownKinds: Record<string, CatalogueKind> = {
  group: "group",
  tile: "tile",
  special: "special",
  folder: "folder",
  geoobject: "geoobject"
};

export const mapRootIdName: Record<CatalogueRoot, MapRootName> = { maps: "MAP", layers: "LAYER" };

export const rootPrefix: Record<CatalogueRoot, string> = { maps: "MAP_", layers: "LAYER_" };

export type NormaliseInput = {
  langId: number;
  maps: MasterDataModel[];
  layers: MasterDataModel[];
  objectLayers?: ObjectLayerInfo[] | null;
  iconUrl: (idName: string, variant?: "selected" | "hovered" | null) => string;
  tileUrlTemplate: (idName: string, noCache: boolean) => string;
  includeHidden?: boolean;
  pruneEmptyGroups?: boolean;
};

export function parseSettings(value2: string | null | undefined): { settings: MapNodeSettings; problem: string | null } {
  if (!value2 || value2.trim().length === 0) return { settings: {}, problem: null };

  try {
    const parsed = JSON.parse(value2) as unknown;

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { settings: {}, problem: "settings are not a JSON object" };

    return { settings: parsed as MapNodeSettings, problem: null };
  } catch (error) {
    return { settings: {}, problem: `settings are not valid JSON: ${error instanceof Error ? error.message : String(error)}` };
  }
}

export function resolveKind(value: string | null | undefined, hasChildren: boolean): { kind: CatalogueKind; known: boolean } {
  const trimmed = (value ?? "").trim().toLowerCase();

  if (trimmed.length === 0) return { kind: hasChildren ? "group" : "geoobject", known: true };

  const kind = knownKinds[trimmed];

  if (kind) return { kind, known: true };

  return { kind: hasChildren ? "group" : "geoobject", known: false };
}

export function nameOf(row: MasterDataModel, langId: number): string {
  const names = row.name?.lang ?? [];
  const index = Math.max(0, langId - 1);
  const preferred = names[index];
  const fallback = names[0];

  return preferred && preferred.trim().length > 0 ? preferred : fallback && fallback.trim().length > 0 ? fallback : row.idName;
}

export function layerFieldsOf(settings: MapNodeSettings, layer: LayerSummary | null | undefined): { bounds: CatalogueBounds | null; hasTable: boolean; layerState: LayerState | null } {
  return {
    bounds: settings.bounds ?? layer?.bounds ?? null,
    hasTable: (typeof settings.tableName === "string" && settings.tableName.length > 0) || layer?.hasTable === true,
    layerState: layer?.state ?? null
  };
}

export function keyOf(idName: string, root: CatalogueRoot): string {
  const prefix = rootPrefix[root];
  const rest = idName.toUpperCase().startsWith(prefix) ? idName.substring(prefix.length) : idName;

  return rest.toLowerCase().replace(/_/g, "-");
}

function hex(r: number | null, g: number | null, b: number | null, a: number | null): string | null {
  if (r === null || g === null || b === null) return null;

  const part = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0").toUpperCase();

  return `#${part(r)}${part(g)}${part(b)}${a === null ? "" : part(a)}`;
}

export function styleOf(row: MasterDataModel, iconUrl: NormaliseInput["iconUrl"]): CatalogueStyle | null {
  const pen = row.attr?.attrPen ?? null;
  const brush = row.attr?.attrBrush ?? null;
  const penColor = pen ? pen.colorRGB1 ?? hex(pen.r, pen.g, pen.b, pen.a) : null;
  const brushColor = brush ? hex(brush.r, brush.g, brush.b, null) : null;

  const penModel: CataloguePen | null = penColor
    ? { color: penColor, width: pen?.width ?? 2, type: pen?.penTypeId ?? null, border: pen ? hex(pen.border_R, pen.border_G, pen.border_B, pen.border_A) : null }
    : null;
  const brushModel: CatalogueBrush | null = brushColor ? { color: brushColor, style: brush?.brushStyleId ?? null, opacity: brush?.a === null || brush?.a === undefined ? 1 : brush.a / 255 } : null;

  let icon: CatalogueIcon | null = null;

  if (row.hasIcon) {
    const meta = row.masterDataIcon;
    const width = meta && meta.width > 0 ? meta.width : 24;
    const height = meta && meta.height > 0 ? meta.height : width;

    icon = {
      url: iconUrl(row.idName),
      selectedUrl: meta?.iconSelected ? iconUrl(row.idName, "selected") : null,
      hoveredUrl: meta?.iconHovered ? iconUrl(row.idName, "hovered") : null,
      width,
      height,
      anchor: [width / 2, height / 2],
      growth: meta?.iconGrowth ?? 1
    };
  }

  if (!penModel && !brushModel && !icon) return null;

  return { pen: penModel, brush: brushModel, icon };
}

export function normaliseTree(root: CatalogueRoot, rows: MasterDataModel[], input: NormaliseInput, problems: CatalogueProblem[]): CatalogueEntry[] {
  const byParent = new Map<number | null, MasterDataModel[]>();
  const ids = new Set(rows.map(a => a.id));
  const rootRows: MasterDataModel[] = [];

  for (const row of rows) {
    const parent = row.parentMasterData_Id !== null && ids.has(row.parentMasterData_Id) ? row.parentMasterData_Id : null;

    if (parent === null) rootRows.push(row);
    else {
      const list = byParent.get(parent) ?? [];
      list.push(row);
      byParent.set(parent, list);
    }
  }

  const ordered = (list: MasterDataModel[]) => [...list].sort((a, b) => a.order_ - b.order_ || a.idName.localeCompare(b.idName));
  const objectLayerById = new Map((input.objectLayers ?? []).map(a => [a.id, a] as const));
  const visited = new Set<number>();

  const build = (row: MasterDataModel, parent: CatalogueEntry | null, inheritedSelection: "single" | "multi"): CatalogueEntry | null => {
    if (visited.has(row.id)) {
      problems.push({ key: keyOf(row.idName, root), problem: "cycle: node reached twice" });
      return null;
    }

    visited.add(row.id);

    if (!row.visible && !input.includeHidden) return null;

    const childRows = ordered(byParent.get(row.id) ?? []);
    const { kind, known } = resolveKind(row.value, childRows.length > 0);
    const { settings, problem } = parseSettings(row.value2);
    const key = keyOf(row.idName, root);
    const entryProblems: string[] = [];

    if (!known) entryProblems.push(`unknown kind ${row.value}`);
    if (problem) entryProblems.push(problem);
    if (childRows.length > 0 && kind !== "group") entryProblems.push("a leaf with children");
    if (root === "layers" && (kind === "tile" || kind === "special")) entryProblems.push(`kind ${kind} is not allowed under LAYER`);

    const selection: "single" | "multi" = settings.selection === "single" ? "single" : inheritedSelection;

    const entry: CatalogueEntry = {
      id: row.id,
      idName: row.idName,
      key,
      root,
      kind,
      name: nameOf(row, input.langId),
      order: row.order_,
      visible: row.visible,
      parentKey: parent?.key ?? null,
      path: parent ? [...parent.path, parent.idName] : [],
      pathNames: parent ? [...parent.pathNames, parent.name] : [],
      depth: parent ? parent.depth + 1 : 0,
      selection,
      children: [],
      collapsed: settings.collapsed ?? false,
      defaultSelected: settings.defaultOn ?? false,
      opacity: typeof settings.opacity === "number" ? Math.min(1, Math.max(0, settings.opacity)) : 1,
      minZoom: settings.minZoom ?? null,
      maxZoom: settings.maxZoom ?? null,
      attribution: settings.attribution ?? null,
      bounds: settings.bounds ?? null,
      style: null,
      tile: null,
      special: null,
      source: null,
      layerState: null,
      tiles: null,
      vector: null,
      legend: settings.legend ? settings.legend.map(a => ({ label: a.label, color: a.color ?? null, fillColor: a.fillColor ?? null, iconUrl: a.icon ?? null })) : null,
      settings,
      rawValue: row.value,
      problems: entryProblems,
      origin: "server"
    };

    if (kind === "group") {
      entry.children = childRows.map(a => build(a, entry, selection)).filter((a): a is CatalogueEntry => a !== null);

      if (entry.children.length === 0 && input.pruneEmptyGroups !== false && !input.includeHidden) return null;
    }

    if (kind === "tile") {
      if (!settings.urlTemplate) entryProblems.push("a Tile node needs urlTemplate");

      entry.tile = {
        url: settings.urlTemplate ?? "",
        subdomains: settings.subdomains ?? "abc",
        attribution: settings.attribution ?? "",
        minZoom: settings.minZoom ?? 0,
        maxZoom: settings.maxZoom ?? settings.maxNativeZoom ?? 19,
        maxNativeZoom: settings.maxNativeZoom ?? settings.maxZoom ?? 19,
        tileSize: settings.tileSize ?? 256,
        detectRetina: settings.detectRetina ?? false,
        dark: settings.dark ?? false,
        thumbnail: settings.thumbnail ?? null
      };
    }

    if (kind === "special") {
      if (!settings.provider) entryProblems.push("a Special node needs provider");

      const options = settings.providerOptions ?? {};

      entry.special = {
        provider: (settings.provider ?? "").toLowerCase(),
        variant: options.variant ?? options.type ?? null,
        apiKey: options.key ?? options.apiKey ?? null,
        options,
        attribution: settings.attribution ?? "",
        dark: settings.dark ?? false,
        thumbnail: settings.thumbnail ?? null
      };
    }

    if (kind === "folder" || kind === "geoobject") {
      const folder = kind === "folder" ? settings.folder ?? `MAP_TYPE_${keyOf(row.idName, root).toUpperCase().replace(/-/g, "_")}` : null;
      const objectLayer = kind === "geoobject" ? objectLayerById.get(row.id) ?? null : null;
      const calculated = layerFieldsOf(settings, row.layer);

      entry.style = kind === "geoobject" ? styleOf(row, input.iconUrl) : null;
      entry.source = {
        folder,
        layer: settings.layer ?? null,
        fields: {},
        typeIdName: kind === "geoobject" ? row.idName : null,
        typeIdNames: kind === "geoobject" ? [row.idName, ...descendantIdNames(row, byParent)] : [],
        counts: objectLayer ? { point: objectLayer.pointCount, line: objectLayer.lineCount, polygon: objectLayer.polygonCount, total: objectLayer.count } : null,
        bounds: calculated.bounds,
        hasTable: calculated.hasTable
      };
      entry.layerState = calculated.layerState;
      entry.bounds = entry.source.bounds;
      entry.minZoom = settings.minZoom ?? null;
      entry.maxZoom = settings.maxZoom ?? null;

      if (root === "maps") {
        entry.tiles = {
          tileUrlTemplate: input.tileUrlTemplate(folder ?? row.idName, settings.noCache === true),
          minZoom: entry.minZoom ?? 0,
          maxZoom: entry.maxZoom ?? 22
        };
      } else {
        entry.vector = {
          node: row.idName,
          simplify: settings.simplify ?? true,
          maxFeatures: settings.maxFeatures ?? 5000,
          cluster: settings.cluster ?? true
        };
      }
    }

    for (const text of entryProblems) problems.push({ key, problem: text });

    return entry;
  };

  const entries = ordered(rootRows)
    .map(a => build(a, null, "multi"))
    .filter((a): a is CatalogueEntry => a !== null);

  reportUnreachableCycles(root, rows, visited, problems);

  return entries;
}

function reportUnreachableCycles(root: CatalogueRoot, rows: MasterDataModel[], visited: Set<number>, problems: CatalogueProblem[]): void {
  const byId = new Map(rows.map(a => [a.id, a] as const));

  for (const row of rows) {
    if (visited.has(row.id)) continue;

    const chain = new Set<number>();
    let current: MasterDataModel | undefined = row;

    while (current !== undefined && !visited.has(current.id) && !chain.has(current.id)) {
      chain.add(current.id);
      current = current.parentMasterData_Id === null ? undefined : byId.get(current.parentMasterData_Id);
    }

    if (current !== undefined && chain.has(current.id)) problems.push({ key: keyOf(current.idName, root), problem: "cycle: node reached twice" });

    for (const id of chain) visited.add(id);
  }
}

function descendantIdNames(row: MasterDataModel, byParent: Map<number | null, MasterDataModel[]>): string[] {
  const result: string[] = [];
  const stack = [...(byParent.get(row.id) ?? [])];

  while (stack.length > 0) {
    const current = stack.pop()!;
    result.push(current.idName);
    stack.push(...(byParent.get(current.id) ?? []));
  }

  return result;
}

function indexEntries(maps: CatalogueEntry[], layers: CatalogueEntry[]): { entries: CatalogueEntry[]; byKey: Map<string, CatalogueEntry> } {
  const entries: CatalogueEntry[] = [];
  const byKey = new Map<string, CatalogueEntry>();

  const collect = (list: CatalogueEntry[]) => {
    for (const entry of list) {
      entries.push(entry);
      byKey.set(catalogueKey(entry.root, entry.key), entry);
      collect(entry.children);
    }
  };

  collect(maps);
  collect(layers);

  return { entries, byKey };
}

export function normaliseCatalogue(input: NormaliseInput): Catalogue {
  const problems: CatalogueProblem[] = [];
  const maps = normaliseTree("maps", input.maps, input, problems);
  const layers = normaliseTree("layers", input.layers, input, problems);

  return { langId: input.langId, maps, layers, ...indexEntries(maps, layers), problems, origin: "server" };
}

function sameBounds(a: CatalogueBounds | null, b: CatalogueBounds | null): boolean {
  return a === b || (a !== null && b !== null && a.minLat === b.minLat && a.minLon === b.minLon && a.maxLat === b.maxLat && a.maxLon === b.maxLon);
}

export function applyLayerChange(catalogue: Catalogue, change: LayerChange): Catalogue | null {
  let changed = false;

  const update = (entry: CatalogueEntry): CatalogueEntry => {
    if (entry.id === change.masterDataId && entry.source !== null) {
      const calculated = layerFieldsOf(entry.settings, change);

      if (calculated.layerState === entry.layerState && calculated.hasTable === entry.source.hasTable && sameBounds(calculated.bounds, entry.source.bounds)) return entry;

      changed = true;

      return { ...entry, bounds: calculated.bounds, layerState: calculated.layerState, source: { ...entry.source, bounds: calculated.bounds, hasTable: calculated.hasTable } };
    }

    const children = entry.children.map(update);

    return children.some((child, index) => child !== entry.children[index]) ? { ...entry, children } : entry;
  };

  const maps = catalogue.maps.map(update);
  const layers = catalogue.layers.map(update);

  if (!changed) return null;

  return { ...catalogue, maps, layers, ...indexEntries(maps, layers) };
}

export function applyLayerSummaries(catalogue: Catalogue, rows: MasterDataModel[]): Catalogue | null {
  let current = catalogue;

  for (const row of rows) {
    if (!row.layer) continue;

    current = applyLayerChange(current, { masterDataId: row.id, idName: row.idName, state: row.layer.state, bounds: row.layer.bounds, hasTable: row.layer.hasTable }) ?? current;
  }

  return current === catalogue ? null : current;
}

type SourceCounts = NonNullable<CatalogueEntry["source"]>["counts"];

function sameCounts(a: SourceCounts, b: SourceCounts): boolean {
  return a === b || (a !== null && b !== null && a.point === b.point && a.line === b.line && a.polygon === b.polygon && a.total === b.total);
}

export function applyCounts(catalogue: Catalogue, counted: Catalogue): Catalogue | null {
  const countsById = new Map<number, SourceCounts>();

  for (const entry of counted.entries) {
    if (entry.source !== null) countsById.set(entry.id, entry.source.counts);
  }

  let changed = false;

  const update = (entry: CatalogueEntry): CatalogueEntry => {
    if (entry.source !== null) {
      const counts = countsById.get(entry.id);

      if (counts === undefined || sameCounts(counts, entry.source.counts)) return entry;

      changed = true;

      return { ...entry, source: { ...entry.source, counts } };
    }

    const children = entry.children.map(update);

    return children.some((child, index) => child !== entry.children[index]) ? { ...entry, children } : entry;
  };

  const maps = catalogue.maps.map(update);
  const layers = catalogue.layers.map(update);

  if (!changed) return null;

  return { ...catalogue, maps, layers, ...indexEntries(maps, layers) };
}

export function hasPendingLayers(catalogue: Catalogue): boolean {
  return catalogue.entries.some(a => a.layerState === "NotCalculated" || a.layerState === "Calculating");
}

export function catalogueIconUrl(api: ForteXApi): NormaliseInput["iconUrl"] {
  return (idName, variant) => api.iconUrl(idName, variant ?? null);
}

export function catalogueTileUrl(api: ForteXApi): NormaliseInput["tileUrlTemplate"] {
  return (idName, noCache) => api.tileUrlTemplate(idName, noCache);
}
